import { useCallback } from 'react';
import type { Layer, PathOptions } from 'leaflet';
import type { GeoJSON } from 'geojson';

export interface LeaseFeature extends GeoJSON.Feature {
  properties: {
    'ARAC_Lot No.': string | null;            // For filtering
    'ARAC_Zone ': string | null;              // Note the space at the end
    'ARAC_Description Type ': string | null;   // Note the space at the end
    holder_name?: string | null;              // For color coding
    [key: string]: any;
  };
}

// Keep the original zone colors for reference
export const zoneColors: Record<string, string> = {
  'PS': '#34d399',    // Public
  'LMR': '#f472b6',   // Low-Medium Density
  'CP': '#f97316',    // Commercial Precinct
  'C': '#fb923c',     // Commercial
  'U': '#a78bfa',     // Utilities
  'NZ': '#9ca3af',    // Not zoned
  'H': '#4ade80',     // Horticultural
  'LI': '#818cf8',    // Light Industrial
  'OR': '#60a5fa',    // Organized Recreation
  'WM': '#22d3ee'     // Water Management
};

// New color mapping for holder names
export const holderColors: Record<string, string> = {
  'NTG-CEO HOUSING': '#e11d48',
  'Anindilyakwa Housing Aboriginal Corporation (AHAC)': '#fb7185',
  'East Arnhem Regional Council (EARC)': '#f59e0b',
  'NTG-IES (subsidiary of Power and Water Corporation-PWC)': '#3b82f6',
  'Anindilyakwa Land Council (ALC)': '#10b981',
  'NTG-Territory Families, Housing and Communities (TFHC)': '#8b5cf6',
  'NTG- Department Corporate and Digital Development (DCDD)': '#6366f1',
  'Other': '#9ca3af'  // Gray color for all other holders
};

// List of specific holder names we want to highlight
const specificHolderNames = [
  'NTG-CEO HOUSING',
  'Anindilyakwa Housing Aboriginal Corporation (AHAC)',
  'East Arnhem Regional Council (EARC)',
  'NTG-IES (subsidiary of Power and Water Corporation-PWC)',
  'Anindilyakwa Land Council (ALC)',
  'NTG-Territory Families, Housing and Communities (TFHC)',
  'NTG- Department Corporate and Digital Development (DCDD)'
];

export function useLeaseFeatures() {
  const style = useCallback((feature: LeaseFeature): PathOptions => {
    // Enhanced debug logging to understand what's happening
    console.log('Style function called with feature:', {
      properties: feature.properties,
      holderName: feature.properties.holder_name,
      allProps: Object.keys(feature.properties)
    });
    
    const holderName = feature.properties.holder_name;
    
    // If holder name is null or undefined, return style with no fill
    if (!holderName) {
      console.log('No holder name found, using transparent fill');
      return {
        weight: 1,
        opacity: 1,
        color: '#374151',
        fillOpacity: 0  // No fill for lots without a holder name
      };
    }
    
    // Check if the holder name is in our specific list
    const isSpecificHolder = specificHolderNames.includes(holderName);
    const fillColor = isSpecificHolder ? holderColors[holderName] : holderColors['Other'];
    
    // Add detailed debug logging
    console.log('Holder name color selection:', {
      holderName,
      isSpecificHolder,
      selectedColor: fillColor,
      allSpecificNames: specificHolderNames
    });
    
    return {
      fillColor,
      weight: 1,
      opacity: 1,
      color: '#374151',
      fillOpacity: 0.7
    };
  }, []);

  const onEachFeature = useCallback((feature: LeaseFeature, layer: Layer) => {
    layer.on({
      mouseover: (e) => {
        const layer = e.target;
        layer.setStyle({
          weight: 2,
          fillOpacity: 0.9
        });
      },
      mouseout: (e) => {
        const layer = e.target;
        layer.setStyle({
          weight: 1,
          fillOpacity: 0.7
        });
      }
    });
  }, []);

  return {
    style,
    onEachFeature
  };
}
